/*
 * Reading an article.
 *
 * The article arrives from kb.ArticlePublished, so everything on this page is by
 * construction something a reader is allowed to see: a hidden, scheduled or
 * unwritten article is a 404 here rather than a page with a warning on it. That is
 * why there is no visibility state to render.
 *
 * The page is addressed by the PAIR /kb/{productSlug}/{slug}, and both route
 * parameters are passed on to every read this page makes - the article, its
 * related list, its images. A slug alone identifies nothing since
 * UQ_Article_ProductId_Slug replaced the global one, so a read that dropped the
 * product segment would be asking "any article called this", which is a question
 * with more than one answer.
 *
 * The two effects are the fiddly part and both exist for a reason: the document
 * title has to be restored on unmount or the browse page inherits the article's
 * title, and an incoming #fragment has to be scrolled to by hand because the hash
 * is already in the URL before the content it points at has been rendered.
 *
 * Images are addressed by the same pair, and for the same reason the article is:
 * the endpoint behind buildImageSrc reads kb.ArticleImagePublished keyed on
 * (product slug, slug, file name), so it can only ever serve an image belonging
 * to an article this page was allowed to load in the first place. There is no
 * second visibility check to forget - a missing image and an image on an article
 * a reader may not see are the same 404, deliberately indistinguishable.
 *
 * PRIVATE ARTICLES (2026-10-26, S46). Every read here is the reader's
 * (usePublicReader): a private article is served to its audiences and to
 * administrators, and is the plain 404 for everybody else - where a signed-out
 * visitor is told that some articles need sign-in (SignInHint). It carries a
 * tag per audience beside its product and categories, and its images are
 * fetched with the reader's token (usePrivateArticleImages), because the image
 * endpoint serves them only to whoever may read the text and an <img> cannot
 * send a token.
 *
 * ATTACHMENTS (2026-10-28, S47). The article's list is shown under the text,
 * and an [[attachment:<id>]] token in the text is a link. Both download THROUGH
 * this article: the page asks the server for a short-lived link with the
 * reader's token - the server signs one only when this reader may read this
 * article and the article lists or links the file - and sends the browser to
 * it. Each link's href is the address that redirects to the same kind of link,
 * so a link opened in a new tab still works for whoever may read the article
 * without signing in; a plain click is taken over so that a signed-in reader's
 * token goes with it.
 *
 * LANGUAGE (S48, 2026-10-29). The page's own words - "Published", the reading
 * time, the sidebar's headings, "Attachments" and "Download", a file's type and
 * size - are the chosen language's; the article itself is as its author wrote it.
 *
 * EDIT LINK (2026-11-04). An administrator sees "Edit this article" above
 * "Signed in as", to /admin/articles/{id} (EditArticleLink.tsx).
 */

import { Link, useParams } from 'react-router-dom';
import { useCallback, useEffect, useRef } from 'react';
import {
  articleHref,
  attachmentLinkNames,
  estimateReadingMinutes,
  extractHeadings,
  parsePublicArticleAttachmentPath,
  publicArticleAttachmentPath,
  type PublicArticle,
} from '@accotool-kb/shared';
import { getArticleBySlug, getAttachmentDownloadLink, getRelatedArticles } from '../api/publicApi.js';
import { useAttachmentDownload, type AttachmentDownloadState } from '../hooks/useAttachmentDownload.js';
import type { PublicReader } from '../api/publicReader.js';
import { forReader } from '../api/publicReader.js';
import { usePublicReader } from '../auth/AuthProvider.js';
import { useAsync } from '../hooks/useAsync.js';
import { AudienceTags, DateTime, ErrorBanner, Loading, Tag } from '../components/ui.js';
import { SignInHint } from './SignInHint.js';
import { usePrivateArticleImages } from './usePrivateArticleImages.js';
import { Markdown } from '../components/Markdown.js';
import { usePublicContext } from './PublicLayout.js';
import { ApiError } from '../api/client.js';
import { SignedInAs } from '../site/SignedInAs.js';
import { EditArticleLink } from './EditArticleLink.js';
import { articlesHeading } from './pageHeadings.js';
import { useLanguage, useMessages } from '../i18n/language.js';
import { richText } from '../i18n/richText.js';

export function ArticlePage(): JSX.Element {
  const params = useParams();
  const { bootstrap } = usePublicContext();
  const { language } = useLanguage();
  const t = language.messages;

  const productSlug = params.productSlug ?? '';
  const slug = params.slug ?? '';

  const reader = usePublicReader();
  const state = useAsync(
    () => forReader(reader, () => getArticleBySlug(productSlug, slug, reader)),
    [productSlug, slug, reader.ready, reader.key],
  );
  const related = useAsync(
    () => forReader(reader, () => getRelatedArticles(productSlug, slug, reader).then((result) => result.items)),
    [productSlug, slug, reader.ready, reader.key],
  );

  const article = state.data;

  /*
   * Image sources are built from the article's OWN address rather than from the
   * two segments this page was reached with. They normalise to the same thing -
   * the server trims and lowercases both at every entry point - but a URL typed
   * as /kb/AccoTOOL-Core/... is accepted and would then put a spelling no row
   * holds into an <img src>, where nothing else normalises it. The response
   * carries the canonical pair; use it, and fall back to the route parameters
   * only for the render before the response has arrived.
   */
  //
  // Memoised inside the hook, because Markdown re-renders the whole body when
  // this identity changes. For a private article (S46) it hands back blob: URLs
  // fetched with the reader's token, and `imagesReady` holds the body back until
  // they are in.
  const { buildImageSrc, ready: imagesReady } = usePrivateArticleImages(article, { productSlug, slug }, reader);

  /*
   * Attachment links (S47), built from the article's own address for the reason
   * the image sources are. Any href in the body that names an attachment download
   * is taken over - including one an author wrote by hand to ANOTHER article's
   * file, which downloads through that article, for this reader.
   */
  const downloads = useAttachmentDownload();
  const { download } = downloads;
  const canonicalProduct = article?.product.slug ?? productSlug;
  const canonicalSlug = article?.slug ?? slug;
  const buildAttachmentHref = useCallback(
    (attachmentId: string) => publicArticleAttachmentPath(canonicalProduct, canonicalSlug, attachmentId),
    [canonicalProduct, canonicalSlug],
  );
  const handleLinkClick = useCallback(
    (href: string): boolean => {
      const target = parsePublicArticleAttachmentPath(href);
      if (!target) return false;
      void download(() => getAttachmentDownloadLink(target.productSlug, target.slug, target.attachmentId, reader));
      return true;
    },
    [download, reader],
  );

  // A failed download is said where the reader is looking - which, for a link in
  // the middle of a long article, is not the top of the page.
  const downloadError = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (downloads.error) downloadError.current?.scrollIntoView({ block: 'nearest' });
  }, [downloads.error]);

  useEffect(() => {
    if (article) document.title = `${article.title} · ${bootstrap.site.title}`;
    return () => {
      document.title = bootstrap.site.title;
    };
  }, [article, bootstrap.site.title]);

  // Anchor links from other articles ([[kb:product/x#heading]]) arrive as a hash
  // that is present before the content is rendered, so the browser cannot scroll
  // to it on its own.
  useEffect(() => {
    if (!article || !window.location.hash) return;
    const id = decodeURIComponent(window.location.hash.slice(1));
    const target = document.getElementById(id);
    target?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [article]);

  if (state.loading && !article) return <Loading label={t.article.loading} />;

  if (state.error) {
    const notFound = state.error instanceof ApiError && state.error.status === 404;
    return (
      <>
        <ErrorBanner error={state.error} {...(notFound ? {} : { onRetry: state.reload })} />
        {notFound && <SignInHint />}
        <p>
          <Link to="/">{t.article.back}</Link>
        </p>
      </>
    );
  }

  if (!article || !imagesReady) return <Loading label={t.article.loading} />;

  const headings = extractHeadings(article.content, {
    attachmentNames: attachmentLinkNames(article.attachmentLinks ?? []),
  }).filter((heading) => heading.level <= 3);

  return (
    <article>
      <header className="article-header">
        <nav className="breadcrumb" aria-label={t.common.breadcrumb}>
          {/* The articles page's own title (2026-10-15) - "All articles" until then -
              as the release notes' link back carries theirs. */}
          <Link to="/">{articlesHeading(bootstrap.site, language.code).title}</Link>
        </nav>

        <h1>{article.title}</h1>

        <div className="article-meta">
          <span>{richText(t.common.publishedOn, { date: <DateTime value={article.publishAtUtc} dateOnly /> })}</span>
          <span aria-hidden="true">·</span>
          <span>{t.article.readingTime(estimateReadingMinutes(article.content))}</span>
          <span aria-hidden="true">·</span>
          <span>{t.article.revision(article.currentRevisionNumber)}</span>
        </div>

        <div className="tag-list" style={{ marginTop: '0.75rem' }}>
          {/* One product, never a list: it is half of this page's address. */}
          <Tag kind="product">{article.product.name}</Tag>
          {article.categoryTags.map((tag) => (
            <Tag key={`c-${tag}`} kind="category">
              {tag}
            </Tag>
          ))}
          {/* Who it is for, on a private article (S46). */}
          <AudienceTags article={article} />
        </div>
      </header>

      <div className="article-body">
        <div className="article-column">
          {/*
            No href builder is passed: renderArticleLinks builds every
            cross-reference with the shared articleHref, from the product and
            slug the SERVER resolved the token to. A link to another article can
            therefore leave this product, which is the point of the qualified
            [[kb:product/slug]] form, and a page that supplied its own template
            could not have spelled that.
          */}
          <Markdown
            content={article.content}
            linkTargets={article.linkTargets}
            attachmentLinks={article.attachmentLinks ?? []}
            buildAttachmentHref={buildAttachmentHref}
            onLinkClick={handleLinkClick}
            buildImageSrc={buildImageSrc}
          />

          <div ref={downloadError}>
            {downloads.error && <ErrorBanner error={downloads.error} onDismiss={downloads.clearError} />}
          </div>

          <ArticleAttachments article={article} reader={reader} downloads={downloads} />
        </div>

        <aside className="sidebar">
          {headings.length > 2 && (
            <nav className="card card-tight sidebar-group" aria-label={t.common.onThisPage}>
              <h3>{t.common.onThisPage}</h3>
              <ul style={{ listStyle: 'none', margin: 0, padding: 0, fontSize: '0.9rem' }}>
                {headings.map((heading) => (
                  <li
                    key={heading.id}
                    style={{ paddingLeft: `${(heading.level - 1) * 0.75}rem`, marginBottom: 4 }}
                  >
                    <a href={`#${heading.id}`}>{heading.text}</a>
                  </li>
                ))}
              </ul>
            </nav>
          )}

          {related.data && related.data.length > 0 && (
            <div className="card card-tight sidebar-group">
              <h3>{t.article.related}</h3>
              <ul style={{ listStyle: 'none', margin: 0, padding: 0, fontSize: '0.9rem' }}>
                {related.data.map((item) => (
                  <li key={item.articleId} style={{ marginBottom: 6 }}>
                    {/* Related articles are not confined to this product, so the
                        link is built from the ROW's product, not the page's. */}
                    <Link to={articleHref(item.product.slug, item.slug)}>{item.title}</Link>
                    {/* A private one says so here too (S46), as in every list. */}
                    {item.isPrivate && (
                      <div className="tag-list" style={{ marginTop: 2 }}>
                        <AudienceTags article={item} />
                      </div>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* For an administrator only, as "Manage" is (2026-11-04). */}
          <EditArticleLink articleId={article.articleId} />

          <SignedInAs />
        </aside>
      </div>
    </article>
  );
}

/**
 * The article's attachment list, under the text (S47) - nothing at all when it
 * lists none. Each entry is a link to the redirecting download address, taken
 * over on a plain click like a link in the text, so that the reader's token goes
 * with it.
 */
function ArticleAttachments({
  article,
  reader,
  downloads,
}: {
  article: PublicArticle;
  reader: PublicReader;
  downloads: AttachmentDownloadState;
}): JSX.Element | null {
  const t = useMessages();
  const attachments = article.attachments ?? [];
  if (attachments.length === 0) return null;

  return (
    <section className="article-attachments" aria-labelledby="article-attachments-heading">
      <h2 id="article-attachments-heading">{t.article.attachments}</h2>
      <ul className="attachment-list">
        {attachments.map((attachment) => (
          <li key={attachment.attachmentId} className="attachment-list-row">
            <div className="attachment-list-detail">
              <span className="attachment-name">{attachment.fileName}</span>
              <span className="faint">
                {t.article.fileType(attachment.fileName, attachment.contentType)} ·{' '}
                {t.article.byteSize(attachment.byteLength)}
              </span>
            </div>
            <div className="button-row">
              <a
                className="button button-small"
                href={publicArticleAttachmentPath(article.product.slug, article.slug, attachment.attachmentId)}
                aria-label={t.article.downloadFile(attachment.fileName)}
                aria-disabled={downloads.running || undefined}
                onClick={(event) => {
                  if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
                  event.preventDefault();
                  if (downloads.running) return;
                  void downloads.download(() =>
                    getAttachmentDownloadLink(article.product.slug, article.slug, attachment.attachmentId, reader),
                  );
                }}
              >
                {t.article.download}
              </a>
            </div>
          </li>
        ))}
      </ul>
    </section>
  );
}
